import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import BasicLayout from "../../layauots/BasicLayout";
import { Card, Alert, AlertTitle, Autocomplete, Checkbox, FormControlLabel, FormGroup, TextField } from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Save } from "react-bootstrap-icons";
import { ExitToApp } from "@mui/icons-material";
import bgImage from "../../../assets/images/bg-sign-up-cover.jpeg";
import { fetchUsuarios } from "../../Utils/Usuarios";
import { fetchOrganismos } from "../../Utils/Organismo";
import { DIAS } from "./calendarioComun";

// Formulario compartido por CalendarioGestionAdd y CalendarioGestionEdit.
//  - Alta (modo "alta"): un gestor, un organismo y uno o varios días: se carga una fila del calendario por cada día elegido.
//  - Edición (modo "edicion"): una sola fila, con un solo día.
// onGuardar recibe { idUsuario, idOrganismo, dias } (alta) o { idUsuario, idOrganismo, diaSemana } (edición) y devuelve la respuesta
// de la API. mensajeExito es un texto o una función que arma el texto a partir de la respuesta.
const CalendarioGestionForm = ({ modo, titulo, descripcion, valoresIniciales, onGuardar, mensajeExito }) => {
  const navigate = useNavigate();
  const esAlta = modo === "alta";

  const [formData, setFormData] = useState(valoresIniciales);
  const [usuarios, setUsuarios] = useState([]);
  const [organismos, setOrganismos] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [exito, setExito] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [nombreboton, setnombreboton] = useState("Cancelar");

  // Las listas desplegables: gestores activos (sin el gestor testigo, que no gestiona casos) y organismos activos. En edición, si el
  // gestor o el organismo de la fila ya no está activo igual se muestra, para que se vea qué tenía.
  useEffect(() => {
    let cancelado = false;
    Promise.all([fetchUsuarios(), fetchOrganismos()]).then(([listaUsuarios, listaOrganismos]) => {
      if (cancelado) return;
      setUsuarios(listaUsuarios.filter((u) => !u.esTestigo && (u.activo || u.idUsuario === valoresIniciales.idUsuario)));
      setOrganismos(listaOrganismos.filter((o) => o.activo === 1 || o.activo === true || o.idOrganismo === valoresIniciales.idOrganismo));
    });
    return () => {
      cancelado = true;
    };
  }, [valoresIniciales.idUsuario, valoresIniciales.idOrganismo]);

  const cambiar = (campo, valor) => setFormData((prev) => ({ ...prev, [campo]: valor }));

  const alternarDia = (valor) =>
    setFormData((prev) => ({
      ...prev,
      dias: prev.dias.includes(valor) ? prev.dias.filter((d) => d !== valor) : [...prev.dias, valor].sort(),
    }));

  const handleVolver = () => navigate("/CalendarioGestionListar");

  const handleSubmit = async () => {
    if (!formData.idUsuario) {
      setMensaje("Elegí un gestor");
      setExito(false);
      return;
    }
    if (!formData.idOrganismo) {
      setMensaje("Elegí un organismo");
      setExito(false);
      return;
    }
    if (esAlta && formData.dias.length === 0) {
      setMensaje("Elegí al menos un día");
      setExito(false);
      return;
    }
    if (!esAlta && !formData.diaSemana) {
      setMensaje("Elegí un día");
      setExito(false);
      return;
    }
    try {
      setGrabando(true);
      setMensaje("");
      const datos = esAlta
        ? { idUsuario: formData.idUsuario, idOrganismo: formData.idOrganismo, dias: formData.dias }
        : { idUsuario: formData.idUsuario, idOrganismo: formData.idOrganismo, diaSemana: formData.diaSemana };
      const res = await onGuardar(datos);
      if (res?.isSuccess) {
        setMensaje(typeof mensajeExito === "function" ? mensajeExito(res) : mensajeExito);
        setExito(true);
        setnombreboton("Volver");
      } else {
        setMensaje(res?.error || "Error al guardar");
        setExito(false);
        setGrabando(false);
      }
    } catch (error) {
      setMensaje(error?.detail || error?.message || "Error en la solicitud");
      setExito(false);
      setGrabando(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card style={{ width: "157%" }}>
        <MDBox variant="gradient" bgColor="secondary" borderRadius="lg" coloredShadow="light" mx={2} mt={-3} p={3} mb={1} textAlign="center">
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {titulo}
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            {descripcion}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={3}>
              <Autocomplete
                options={usuarios}
                getOptionLabel={(u) => u.nombreCompleto ?? ""}
                isOptionEqualToValue={(a, b) => a.idUsuario === b.idUsuario}
                value={usuarios.find((u) => u.idUsuario === formData.idUsuario) ?? null}
                onChange={(e, valor) => cambiar("idUsuario", valor?.idUsuario ?? null)}
                noOptionsText="No hay gestores"
                renderInput={(params) => <TextField {...params} label="Gestor" />}
              />
            </MDBox>
            <MDBox mb={3}>
              <Autocomplete
                options={organismos}
                getOptionLabel={(o) => o.descripcion ?? ""}
                isOptionEqualToValue={(a, b) => a.idOrganismo === b.idOrganismo}
                value={organismos.find((o) => o.idOrganismo === formData.idOrganismo) ?? null}
                onChange={(e, valor) => cambiar("idOrganismo", valor?.idOrganismo ?? null)}
                noOptionsText="No hay organismos"
                renderInput={(params) => <TextField {...params} label="Organismo" />}
              />
            </MDBox>
            <MDBox mb={2}>
              {esAlta ? (
                <>
                  <MDTypography variant="button" fontWeight="medium" color="text" display="block">
                    Días en que le toca este organismo
                  </MDTypography>
                  <FormGroup row>
                    {DIAS.map((dia) => (
                      <FormControlLabel
                        key={dia.valor}
                        control={<Checkbox checked={formData.dias.includes(dia.valor)} onChange={() => alternarDia(dia.valor)} />}
                        label={dia.nombre}
                      />
                    ))}
                  </FormGroup>
                  <MDButton variant="text" color="info" size="small" onClick={() => cambiar("dias", DIAS.map((d) => d.valor))}>
                    Lunes a viernes
                  </MDButton>
                  <MDButton variant="text" color="info" size="small" onClick={() => cambiar("dias", [])}>
                    Ninguno
                  </MDButton>
                </>
              ) : (
                <Autocomplete
                  options={DIAS}
                  getOptionLabel={(d) => d.nombre}
                  isOptionEqualToValue={(a, b) => a.valor === b.valor}
                  value={DIAS.find((d) => d.valor === formData.diaSemana) ?? null}
                  onChange={(e, valor) => cambiar("diaSemana", valor?.valor ?? null)}
                  renderInput={(params) => <TextField {...params} label="Día" />}
                />
              )}
            </MDBox>
            <MDBox mb={1} style={{ display: "flex", gap: "16px" }}>
              <MDButton onClick={handleSubmit} variant="gradient" color="success" endIcon={<Save />} disabled={grabando} fullWidth>
                Grabar
              </MDButton>
              <MDButton onClick={handleVolver} variant="gradient" color="error" endIcon={<ExitToApp />} fullWidth>
                {nombreboton}
              </MDButton>
            </MDBox>
          </MDBox>

          {mensaje && (
            <Alert severity={exito ? "success" : "error"}>
              <AlertTitle>{exito ? "Felicitaciones" : "Error"}</AlertTitle>
              {mensaje}
            </Alert>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
};

export default CalendarioGestionForm;
